import request from './request'

export interface DicItemVo {
  dicVal: string
  dicName: string
  srt?: number
}

export default {
  /** 数据字典（DATA_DIC_TYPE / DATA_DIC） */
  dict: {
    byType: (typeCode: string) =>
      request.get<DicItemVo[]>(
        '/dict/by-type',
        { typeCode },
        { noLoading: true }
      )
  },
  /** 门户首页（匿名可访问接口需在 USER_MENU_API_D 配置 MENU_PRIV_FLAG=0） */
  portal: {
    listHomeFaq: () =>
      request.get('/portal/homeFaq/list', {}, { noLoading: true }),
    listPurchasePlans: () =>
      request.get('/portal/purchase/listPlans', {}, { noLoading: true }),
    payChannels: () =>
      request.get('/portal/purchase/payChannels', {}, { noLoading: true }),
    purchaseSessionState: () =>
      request.get('/portal/purchase/sessionState', {}, { noLoading: true }),
    getPendingReleaseNote: () =>
      request.get('/portal/releaseNote/pending', {}, { noLoading: true }),
    ackReleaseNote: (noteId: number) =>
      request.post(
        '/portal/releaseNote/ack',
        {},
        { params: { noteId }, noLoading: true }
      ),
    getPendingAnnouncement: () =>
      request.get(
        '/portal/message/pendingAnnouncement',
        {},
        { noLoading: true }
      ),
    dismissAnnouncement: (msgId: number) =>
      request.post(
        '/portal/message/dismissAnnouncement',
        {},
        { params: { msgId }, noLoading: true }
      ),
    messageUnreadCount: () =>
      request.get('/portal/message/unreadCount', {}, { noLoading: true }),
    messageHistory: (params: Record<string, unknown>) =>
      request.get('/portal/message/history', params, { noLoading: true }),
    messageDetail: (params: { msgId: number }) =>
      request.get('/portal/message/detail', params, { noLoading: true }),
    messageMarkRead: (msgId: number) =>
      request.post(
        '/portal/message/markRead',
        {},
        { params: { msgId }, noLoading: true }
      ),
    messageMarkAllRead: () =>
      request.post(
        '/portal/message/markAllRead',
        {},
        { noLoading: true, noError: true }
      ),
    listPromotions: () =>
      request.get(
        '/portal/message/listPromotions',
        {},
        { noLoading: true, noError: true }
      )
  },
  feedback: {
    moduleOptions: () =>
      request.get('/portal/feedback/moduleOptions', {}, { noLoading: true }),
    uploadImage: data =>
      request.post('/portal/feedback/uploadImage', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      }),
    submit: data => request.post('/portal/feedback/submit', data),
    myList: data => request.post('/portal/feedback/myList', data),
    unreadCount: () =>
      request.get('/portal/feedback/unreadCount', {}, { noLoading: true }),
    detail: params => request.get('/portal/feedback/detail', params),
    appendMessage: data => request.post('/portal/feedback/appendMessage', data)
  },
  promptLibrary: {
    list: (data: {
      pageNum: number
      pageSize: number
      businessType?: string
      sourceType?: string
      keyword?: string
    }) => request.post('/prompt-library/list', data, { noLoading: true })
  },
  translation: {
    projectPage: (params: Record<string, unknown>) =>
      request.get('/pt/patent-trans-result/page', params, { noLoading: true }),
    projectGet: (id: number | string) =>
      request.get(`/pt/patent-trans-result/${id}`, {}, { noLoading: true }),
    projectCreate: (data: Record<string, unknown>) =>
      request.post('/pt/patent-trans-result', data),
    projectUpdate: (id: number | string, data: Record<string, unknown>) =>
      request.put(`/pt/patent-trans-result/${id}`, data),
    projectDelete: (id: number | string) =>
      request.delete(`/pt/patent-trans-result/${id}`),
    projectDeleteBatch: (ids: Array<number | string>) =>
      request.service.delete('/pt/patent-trans-result/batch', {
        data: { ids }
      }),
    projectTranslate: (id: number | string, data?: Record<string, unknown>) =>
      request.post(`/pt/patent-trans-result/${id}/translate`, data || {}, {
        timeout: 30000
      }),
    projectStatus: (id: number | string, taskId?: string) =>
      request.get(
        `/pt/patent-trans-result/${id}/translate/status`,
        taskId ? { taskId } : undefined,
        { noLoading: true, noError: true }
      ),
    projectVersions: (id: number | string) =>
      request.get(`/pt/patent-trans-result/${id}/versions`),
    projectVersionCreate: (
      id: number | string,
      data: Record<string, unknown>
    ) => request.post(`/pt/patent-trans-result/${id}/versions`, data),
    projectVersionRestore: (id: number | string, versionId: number | string) =>
      request.post(
        `/pt/patent-trans-result/${id}/versions/${versionId}/restore`,
        {}
      ),
    projectRetranslate: (id: number | string, data: Record<string, unknown>) =>
      request.post(`/pt/patent-trans-result/${id}/retranslate`, data, {
        timeout: 120000
      }),
    projectProofreadItems: (id: number | string) =>
      request.get(`/pt/patent-trans-result/${id}/proofread/items`),
    projectProofreadRun: (
      id: number | string,
      data: Record<string, unknown> = {}
    ) =>
      request.post(`/pt/patent-trans-result/${id}/proofread/run`, data, {
        timeout: 180000
      }),
    projectProofreadLatest: (id: number | string) =>
      request.get(`/pt/patent-trans-result/${id}/proofread/latest`),
    projectProofreadResolve: (
      id: number | string,
      data: Record<string, unknown>
    ) => request.post(`/pt/patent-trans-result/${id}/proofread/resolve`, data),
    projectProofreadAiCorrect: (
      id: number | string,
      data: Record<string, unknown>
    ) =>
      request.post(`/pt/patent-trans-result/${id}/proofread/ai-correct`, data, {
        timeout: 120000
      }),
    projectFormalityItems: (id: number | string) =>
      request.get(`/pt/patent-trans-result/${id}/fde/items`),
    projectFormalityRun: (
      id: number | string,
      data: Record<string, unknown> = {}
    ) =>
      request.post(`/pt/patent-trans-result/${id}/fde/check`, data, {
        timeout: 180000
      }),
    projectFormalityLatest: (id: number | string) =>
      request.get(`/pt/patent-trans-result/${id}/fde/latest`),
    projectImageFiles: (id: number | string) =>
      request.get(`/pt/patent-trans-result/${id}/img-files`),
    projectImageUpload: (
      id: number | string,
      data: FormData,
      imageType = '2'
    ) =>
      request.post(`/pt/patent-trans-result/${id}/img-files/upload`, data, {
        params: { imageType },
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
    projectImageCloneToTarget: (
      id: number | string,
      imageId: number | string
    ) =>
      request.post(
        `/pt/patent-trans-result/${id}/img-files/${imageId}/clone-target`
      ),
    projectImageDrawioBootstrap: (
      id: number | string,
      imageId: number | string
    ) =>
      request.get(
        `/pt/patent-trans-result/${id}/img-files/${imageId}/drawio-bootstrap`
      ),
    projectImageDiagramSave: (
      id: number | string,
      imageId: number | string,
      data: Record<string, unknown>
    ) =>
      request.put(
        `/pt/patent-trans-result/${id}/img-files/${imageId}/diagram`,
        data,
        { timeout: 120000 }
      ),
    projectImageDiagramBatchSave: (
      id: number | string,
      data: Record<string, unknown>
    ) =>
      request.put(
        `/pt/patent-trans-result/${id}/img-files/diagram-batch`,
        data,
        { timeout: 120000 }
      ),
    projectExportWord: (id: number | string, data: Record<string, unknown>) =>
      request.service.post(`/pt/patent-trans-result/${id}/export-word`, data, {
        responseType: 'blob',
        timeout: 300000
      }),
    wizardUpload: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return request.post('/pt/patent-trans-wizard/upload-temp', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      })
    },
    wizardParseMeta: (data: Record<string, unknown>) =>
      request.post('/pt/patent-trans-wizard/parse-meta', data, {
        timeout: 120000
      }),
    wizardPreflight: (data: Record<string, unknown>) =>
      request.post('/pt/patent-trans-wizard/preflight', data, {
        timeout: 30000
      }),
    wizardPreflightStatus: (taskId: string) =>
      request.get(
        '/pt/patent-trans-wizard/preflight/status',
        { taskId },
        { noLoading: true, noError: true, timeout: 30000 }
      ),
    wizardTermsPreflight: (data: Record<string, unknown>) =>
      request.post('/pt/patent-trans-wizard/preflight/terms-sentences', data, {
        timeout: 120000
      }),
    wizardSave: (data: Record<string, unknown>) =>
      request.post('/pt/patent-trans-wizard/wizard-save', data),
    wizardDraft: (id: number | string) =>
      request.get(`/pt/patent-trans-wizard/draft/${id}`),
    wizardImportPersonalTerms: (
      id: number | string,
      items: Array<Record<string, unknown>>
    ) =>
      request.post(
        `/pt/patent-trans-wizard/draft/${id}/personal-kb-terms/batch-import`,
        { items },
        { timeout: 120000 }
      ),
    wizardCreate: (data: Record<string, unknown>) =>
      request.post('/pt/patent-trans-wizard/create', data),
    wizardTranslate: (
      id: number | string,
      data: Record<string, unknown> = {}
    ) =>
      request.post(`/pt/patent-trans-wizard/project/${id}/translate`, data, {
        timeout: 30000
      }),
    wizardTranslateStatus: (id: number | string, taskId: string) =>
      request.get(
        `/pt/patent-trans-wizard/project/${id}/translate/status`,
        { taskId },
        { noLoading: true, noError: true, timeout: 30000 }
      ),
    projectFormOptions: () =>
      request.get('/pt/patent-trans-wizard/form-options'),
    personalTermPage: (params: Record<string, unknown>) =>
      request.get('/kb/personal/term/page', params),
    personalTermCreate: (data: Record<string, unknown>) =>
      request.post('/kb/personal/term', data),
    personalTermUpdate: (id: number | string, data: Record<string, unknown>) =>
      request.put(`/kb/personal/term/${id}`, data),
    personalTermDelete: (id: number | string) =>
      request.delete(`/kb/personal/term/${id}`),
    personalTermStatus: (id: number | string, status: string) =>
      request.patch(`/kb/personal/term/${id}/status`, { status }),
    fieldTree: () =>
      request.get('/kb/know-patent-field/tree', {}, { noLoading: true }),
    fieldPage: (params: Record<string, unknown>) =>
      request.get('/kb/know-patent-field/page', params, { noLoading: true }),
    fieldCreate: (data: Record<string, unknown>) =>
      request.post('/kb/know-patent-field', data),
    fieldUpdate: (id: number | string, data: Record<string, unknown>) =>
      request.put(`/kb/know-patent-field/${id}`, data),
    fieldDelete: (id: number | string) =>
      request.delete(`/kb/know-patent-field/${id}`),
    fieldStatus: (id: number | string, status: string) =>
      request.patch(`/kb/know-patent-field/${id}/status`, { status }),
    knowledgeTermPage: (params: Record<string, unknown>) =>
      request.get('/kb/know-term/page', params, { noLoading: true }),
    knowledgeTermCreate: (data: Record<string, unknown>) =>
      request.post('/kb/know-term', data),
    knowledgeTermUpdate: (id: number | string, data: Record<string, unknown>) =>
      request.put(`/kb/know-term/${id}`, data),
    knowledgeTermDelete: (id: number | string) =>
      request.delete(`/kb/know-term/${id}`),
    knowledgeTermStatus: (id: number | string, status: string) =>
      request.patch(`/kb/know-term/${id}/status`, { status }),
    knowledgeSentencePage: (params: Record<string, unknown>) =>
      request.get('/kb/know-sentence/page', params, { noLoading: true }),
    knowledgeSentenceCreate: (data: Record<string, unknown>) =>
      request.post('/kb/know-sentence', data),
    knowledgeSentenceUpdate: (
      id: number | string,
      data: Record<string, unknown>
    ) => request.put(`/kb/know-sentence/${id}`, data),
    knowledgeSentenceDelete: (id: number | string) =>
      request.delete(`/kb/know-sentence/${id}`),
    knowledgeSentenceStatus: (id: number | string, status: string) =>
      request.patch(`/kb/know-sentence/${id}/status`, { status }),
    knowledgePatentPage: (params: Record<string, unknown>) =>
      request.get('/kb/know-patent-trans/page', params, { noLoading: true }),
    knowledgePatentCreate: (data: Record<string, unknown>) =>
      request.post('/kb/know-patent-trans', data),
    knowledgePatentBatchCreate: (data: {
      rows: Array<Record<string, unknown>>
    }) =>
      request.post('/kb/know-patent-trans/batch', data, {
        timeout: 120000,
        noLoading: true
      }),
    knowledgePatentUpdate: (
      id: number | string,
      data: Record<string, unknown>
    ) => request.put(`/kb/know-patent-trans/${id}`, data),
    knowledgePatentDelete: (id: number | string) =>
      request.delete(`/kb/know-patent-trans/${id}`),
    knowledgePatentStatus: (id: number | string, status: string) =>
      request.patch(`/kb/know-patent-trans/${id}/status`, { status }),
    adminPromptPage: (params: Record<string, unknown>) =>
      request.get('/admin/know-prompt/page', params, { noLoading: true }),
    adminPromptCreate: (data: Record<string, unknown>) =>
      request.post('/admin/know-prompt', data),
    adminPromptUpdate: (id: number | string, data: Record<string, unknown>) =>
      request.put(`/admin/know-prompt/${id}`, data),
    adminPromptDelete: (id: number | string) =>
      request.delete(`/admin/know-prompt/${id}`),
    adminPromptStatus: (id: number | string, status: string) =>
      request.patch(`/admin/know-prompt/${id}/status`, { status }),
    adminFwagPage: (params: Record<string, unknown>) =>
      request.get('/admin/know-fwag-prompt/page', params, { noLoading: true }),
    adminFwagCreate: (data: Record<string, unknown>) =>
      request.post('/admin/know-fwag-prompt', data),
    adminFwagUpdate: (id: number | string, data: Record<string, unknown>) =>
      request.put(`/admin/know-fwag-prompt/${id}`, data),
    adminFwagDelete: (id: number | string) =>
      request.delete(`/admin/know-fwag-prompt/${id}`),
    adminFwagStatus: (id: number | string, status: string) =>
      request.patch(`/admin/know-fwag-prompt/${id}/status`, { status }),
    adminFdePage: (params: Record<string, unknown>) =>
      request.get('/admin/fde-itme/page', params, { noLoading: true }),
    adminFdeCreate: (data: Record<string, unknown>) =>
      request.post('/admin/fde-itme', data),
    adminFdeUpdate: (id: number | string, data: Record<string, unknown>) =>
      request.put(`/admin/fde-itme/${id}`, data),
    adminFdeDelete: (id: number | string) =>
      request.delete(`/admin/fde-itme/${id}`),
    adminFdeStatus: (id: number | string, status: string) =>
      request.patch(`/admin/fde-itme/${id}/status`, { status }),
    adminPrfPage: (params: Record<string, unknown>) =>
      request.get('/admin/prf-itme/page', params, { noLoading: true }),
    adminPrfCreate: (data: Record<string, unknown>) =>
      request.post('/admin/prf-itme', data),
    adminPrfUpdate: (id: number | string, data: Record<string, unknown>) =>
      request.put(`/admin/prf-itme/${id}`, data),
    adminPrfDelete: (id: number | string) =>
      request.delete(`/admin/prf-itme/${id}`),
    adminPrfStatus: (id: number | string, status: string) =>
      request.patch(`/admin/prf-itme/${id}/status`, { status }),
    adminOaPage: (params: Record<string, unknown>) =>
      request.get('/admin/oa-prompt/page', params, { noLoading: true }),
    adminOaCreate: (data: Record<string, unknown>) =>
      request.post('/admin/oa-prompt', data),
    adminOaUpdate: (id: number | string, data: Record<string, unknown>) =>
      request.put(`/admin/oa-prompt/${id}`, data),
    adminOaDelete: (id: number | string) =>
      request.delete(`/admin/oa-prompt/${id}`),
    adminOaStatus: (id: number | string, status: string) =>
      request.patch(`/admin/oa-prompt/${id}/status`, { status }),
    adminOcrPage: (params: Record<string, unknown>) =>
      request.get('/admin/ocr-prompt/page', params, { noLoading: true }),
    adminOcrCreate: (data: Record<string, unknown>) =>
      request.post('/admin/ocr-prompt', data),
    adminOcrUpdate: (id: number | string, data: Record<string, unknown>) =>
      request.put(`/admin/ocr-prompt/${id}`, data),
    adminOcrDelete: (id: number | string) =>
      request.delete(`/admin/ocr-prompt/${id}`),
    adminOcrStatus: (id: number | string, status: string) =>
      request.patch(`/admin/ocr-prompt/${id}/status`, { status })
  },
  application: {
    submit: data => request.post('/security/userApply/submit', data)
  },
  register: {
    sendSms: (data, config = {}) =>
      request.post('/security/register/sendsms', data, config),
    submit: (data, config = {}) =>
      request.post('/security/register/submit', data, config)
  },
  login: {
    //登录相关接口
    getVerificationCode: data => request.post('security/sendsms', data), //获取验证码
    login: data => request.post('security/login', data), //登录接口
    checkEnterprise: params => request.get('security/changeentp', params), //切换企业
    getUserInfo: (config = {}) =>
      request.get('security/getUserInfo', {}, config), //获取用户详情接口
    logOut: () => request.post('security/logout') //退出登录接口
    // getVerificationCode: data => request.post('security/sendsms', data), //获取验证码
    // login: data => request.post('security/login', data), //登录接口
    // // checkEnterprise: params => request.get('security/changeentp', params), //切换企业
    // getUserInfo: params => request.get('security/getUserInfo', params), //获取用户详情接口
    // logOut: () => request.post('security/logout'), //退出登录接口
    // updatePassword: data => request.post('/security/updatePwd', data), //修改密码
    // getEnableOtp: () => request.get('/security/enableOtp'), //OTP启用获取二维码
    // getOTPVerify: params => request.get('/security/verifyOtpCode', params), //OTP验证
    // getEnableOtpVerify: params =>
    //   request.get('/security/enableOtpVerifyOtpCode', params), //启用调用OTP验证
    // getDisableOtpVerify: params =>
    //   request.get('/security/disableOtpVerifyOtpCode', params) //禁用调用OTP验证
  },
  //专利挖掘
  patentMining: {
    getTocken: () =>
      request.get('/isi/getIsiToken', {}, { loadingMode: 'silent' }), //获取语音识别的tocken
    createProject: data =>
      request.post('/mine/pateMineProjectB/add', data, { loadingMode: 'mutate' }), //创建项目
    getProjectList: data =>
      request.post('/mine/pateMineProjectB/list', data, { loadingMode: 'query' }), //获取项目列表（页内区域 Loading）
    deleteProject: params =>
      request.get('/mine/pateMineProjectB/delete', params, {
        loadingMode: 'mutate'
      }), //删除项目
    batchDeleteProject: data =>
      request.post('/mine/pateMineProjectB/batchDelete', data, {
        loadingMode: 'mutate'
      }), //批量删除项目
    updateProject: data =>
      request.post('/mine/pateMineProjectB/update', data, {
        loadingMode: 'mutate'
      }), //修改项目信息（不传 step）
    getFileDetail: data =>
      request.post('/mine/pateMineProjectB/getFileInfo', data, {
        loadingMode: 'query'
      }), //点击左侧项目列表具体文件获取详情（页内 Loading 由挖掘编辑区承担）
    // ------------------------------------------------------------------------------------------
    //第一步
    batchUpload: data =>
      request.post('/mine/pateMineTechnicalFileC/batchUpload', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        loadingMode: 'mutate'
      }), //第一步的上传交底材
    getBriefingMaterialList: data =>
      request.post('/mine/pateMineTechnicalFileC/listByProId', data, {
        loadingMode: 'query'
      }), //获取第一步交底材料的列表
    aiAnalyzeOne: data =>
      request.post('/mine/pateMineTechnicalFileC/aiAnalyze', data, {
        timeout: 300000,
        loadingMode: 'mutate'
      }), //第一步交底材料的ai解析
    deleteMaterialFile: data =>
      request.post('/mine/pateMineTechnicalFileC/deleteById', data, {
        loadingMode: 'mutate'
      }), //删除第一部分的交底材料文件
    batchDeleteCurrentPageImages: data =>
      request.post('/mine/pateMineTechnicalFileC/batchDeleteCurrentPage', data, {
        loadingMode: 'mutate'
      }), //第一步解析图片列表批量删除
    saveStepOne: data =>
      request.post('/mine/pateMineTechnicalFileC/save', data, {
        timeout: 300000,
        loadingMode: 'mutate'
      }), //第一步的保存接口（含下一步时服务端按需 AI 解析，与 aiAnalyze 同超时）
    // ------------------------------------------------------------------------------------------
    //第二部保存
    uploadTwoFile: data =>
      request.post('/mine/pateMineReferenceC/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        loadingMode: 'mutate'
      }), //第二部上传参考文件
    getTwoFileList: data =>
      request.post('/mine/pateMineReferenceC/listByProId', data, {
        loadingMode: 'query'
      }), //获取第二部上传文件的列表
    deleteTwoFile: data =>
      request.post('/mine/pateMineReferenceC/delete', data, {
        loadingMode: 'mutate'
      }), //第二部删除文件列表接口
    saveStepTwo: params =>
      request.get('/mine/pateMineMessageC/aiCreateMsg', params, {
        timeout: 300000,
        loadingMode: 'mutate'
      }), //第二部保存接口
    // ------------------------------------------------------------------------------------------
    //第三步问答列表
    getProblemList: params =>
      request.get('/mine/pateMineMessageC/list', params, {
        loadingMode: 'query'
      }), //获取第三步的问答列表
    saveStepThree: data =>
      request.post('/mine/pateMineMessageC/save', data, {
        loadingMode: 'mutate'
      }), //第三步问答题的保存
    // ------------------------------------------------------------------------------------------
    //第四步
    getVersionList: params =>
      request.get('/mine/pateMineResultHisD/hisList', params, {
        loadingMode: 'query'
      }), //获取交底书版本列表
    createVersion: data =>
      request.post('/mine/pateMineResultHisD/addNew', data, {
        loadingMode: 'mutate'
      }), //创建版本
    getBriefingContent: data =>
      request.post('/mine/pateMineResultHisD/getInfo', data, {
        loadingMode: 'query'
      }), //获取交底内容（页内 Loading 由挖掘编辑区承担）
    createBriefing: params =>
      request.get('/mine/pateMineResultHisD/aiCreatePmd', params, {
        timeout: 60000,
        loadingMode: 'silent'
      }), // 异步生成交底书；轮询 getPmdGenStatus。后端 prompt 说明见 patent-user/docs/patent-mining-disclosure-ai-input.md
    getPmdGenStatus: params =>
      request.get('/mine/pateMineResultHisD/pmdGenStatus', params, {
        timeout: 30000,
        loadingMode: 'silent'
      }), // 交底书异步生成状态（轮询）
    updateBriefing: data =>
      request.post('/mine/pateMineResultHisD/update', data, {
        loadingMode: 'mutate'
      }), //修改交底书内容保存
    // ------------------------------------------------------------------------------------------
    //ai检索相关接口
    getAiRetrieveList: data =>
      request.post('/otherApi/getPateMineList', data, {
        timeout: 300000,
        loadingMode: 'mutate'
      }), //立即检索（可选传 proId、version 以写入服务端缓存）
    /** 读取当前项目与交底书版本下上次「立即检索」的缓存列表 */
    getAiRetrieveCachedList: params =>
      request.get('/mine/pateMineAiRetrieveD/cachedList', params, {
        loadingMode: 'query'
      }),
    aiCompareAnalysis: data =>
      request.post('/mine/pateMineCompareResultHisD/aiCompare', data, {
        timeout: 300000,
        loadingMode: 'mutate'
      }), // ai检索对比分析（页内状态，不盖全屏）
    getAiAnalysisResult: data =>
      request.post('/mine/pateMineCompareResultHisD/compareList', data, {
        loadingMode: 'silent'
      }), // 获取ai对比结果（静默刷新）
    getAiAnalysisStatus: params =>
      request.get('/mine/pateMineCompareResultHisD/getCompareStatus', params, {
        loadingMode: 'silent'
      }), // 轮询状态，禁用全局 Loading
    getAiAnalysisPdf: params =>
      request.get('/otherApi/getPateMinePdf', params, { loadingMode: 'query' }), //获取检索文件的pdf文件
    // ------------------------------------------------------------------------------------------
    //ai对话相关接口
    getAiDialogueList: data =>
      request.post('/mine/pateAiDialogHisD/list', data, {
        loadingMode: 'query'
      }), //获取ai对话列表接口
    clearHisDialogue: params =>
      request.get('/mine/pateAiDialogHisD/cleanHis', params, {
        loadingMode: 'mutate'
      }), //创建新对话
    resumeDialogSession: params =>
      request.get('/mine/pateAiDialogHisD/resumeSession', params, {
        loadingMode: 'query'
      }), //恢复历史会话
    /** 附图编排历史：按 OSS 键读取临时 Draw.io XML（调用方已有画布/对话反馈） */
    getFigureOrchestrationDrawioXmlContent: params =>
      request.get('/mine/pateFigureOrchestrator/drawioXmlContent', params, {
        loadingMode: 'silent'
      }),
    /** 快捷画图：按已保存附图数量取下一个图号（图N） */
    getNextFigureNum: (params, config = {}) =>
      request.get('/mine/pateFigureOrchestrator/nextFigureNum', params, {
        loadingMode: 'silent',
        ...config
      }),
    /** 挖掘/附图 AI 面板空状态快捷预设（pate_ai_prompt_cfg_d，CHAT_PANEL） */
    getAiPresetQuestions: (params, config = {}) =>
      request.get('/mine/pateAiPromptCfg/presetQuestions', params, {
        loadingMode: 'silent',
        ...config
      }),
    /** 附图 AI 对话快捷绘图按钮（CHAT_PANEL / PRESET_FIGURE_QUICK_ACTION） */
    getFigureQuickActions: (config = {}) =>
      request.get(
        '/mine/pateAiPromptCfg/figureQuickActions',
        {},
        {
          loadingMode: 'silent',
          ...config
        }
      ),
    // createAiDialogue: data =>
    //   request.post('/mine/pateAiDialogHisD/aiDailog', data, {
    //     timeout: 300000
    //   }) //ai对话问答开始
    // ------------------------------------------------------------------------------------------
    //图片相关接口
    getImageList: params =>
      request.get('/mine/pateMineImgManageC/imgList', params, {
        loadingMode: 'query'
      }),
    /** 图示无 diagramCode 时：服务端读 OSS 返回 data URI 供 Draw.io 内联 */
    getFigurePreviewDataUri: params =>
      request.get('/mine/pateMineImgManageC/figurePreviewDataUri', params, {
        loadingMode: 'query'
      }),
    uploadImage: data =>
      request.post('/mine/pateMineImgManageC/batchUpload', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        loadingMode: 'mutate'
      }), //上传图片（与第一步 batchUpload 一致，大图易超 10s/30s）
    deleteImage: data =>
      request.post('/mine/pateMineImgManageC/delete', data, {
        loadingMode: 'mutate'
      }), //删除图片
    batchDeleteImage: data =>
      request.post('/mine/pateMineImgManageC/batchDelete', data, {
        loadingMode: 'mutate'
      }), //图片批量删除
    saveImage: data =>
      request.post('/mine/pateMineImgManageC/save', data, {
        timeout: 300000,
        loadingMode: 'mutate'
      }), //新增图示（save 仅新增；提交完整 draw.io xml）
    updateImage: data =>
      request.post('/mine/pateMineImgManageC/update', data, {
        timeout: 300000,
        loadingMode: 'mutate'
      }), //更新图示（覆盖已有 imgId）
    getAlreadyImageList: data =>
      request.post('/mine/pateMineTechnicalFileC/imgListByProId', data, {
        loadingMode: 'query'
      }), //获取已有的图片列表
    /** 按 fileId 解析技术文件 OSS 访问 URL（图示 XML 中 pmfile:// 占位符用） */
    technicalFileAccessUrl: data =>
      request.post('/mine/pateMineTechnicalFileC/accessUrl', data, {
        noError: true,
        loadingMode: 'silent'
      }),
    /**
     * 批量获取技术文件访问URL
     * @param data - 请求参数，包含需要获取访问链接的文件信息
     * @returns Promise<any> - 返回包含文件访问URL列表的响应数据
     */
    technicalFileAccessUrlBatch: data =>
      request.post('/mine/pateMineTechnicalFileC/accessUrlBatch', data, {
        noError: true,
        loadingMode: 'silent'
      }),
    /** 参考文件内嵌图 OSS 访问 URL（与 technicalFileAccessUrl 结构相同，查参考文件表） */
    referenceFileAccessUrl: data =>
      request.post('/mine/pateMineReferenceC/accessUrl', data, {
        noError: true,
        loadingMode: 'silent'
      }),
    referenceFileAccessUrlBatch: data =>
      request.post('/mine/pateMineReferenceC/accessUrlBatch', data, {
        noError: true,
        loadingMode: 'silent'
      }),
    getMajorFieldList: () =>
      request.post('/mine/pateScopeA/list', {}, { loadingMode: 'query' }), //专业领域
    /** 附图标记（动词风格，与专利撰写一致） */
    figureMarkerList: (proId: number | string, fileId?: number | string) =>
      request.post(
        '/mine/pateMineFigureMarkerC/list',
        {
          proId,
          ...(fileId != null && fileId !== '' ? { fileId } : {})
        },
        { loadingMode: 'query' }
      ),
    figureMarkerBatchSave: (
      proId: number | string,
      data: { items: unknown[] }
    ) =>
      request.post(
        '/mine/pateMineFigureMarkerC/batchSave',
        {
          proId,
          items: data.items
        },
        { loadingMode: 'mutate' }
      ),
    figureMarkerSave: (
      proId: number | string,
      data: {
        id?: number | null
        refCode: string
        labelZh: string
        fileId?: number | string
        canvasXml?: string
      }
    ) =>
      request.post(
        '/mine/pateMineFigureMarkerC/save',
        {
          proId,
          ...data
        },
        { noError: true, loadingMode: 'mutate' }
      ),
    figureMarkerDeleteById: (
      proId: number | string,
      data: { id: number; fileId?: number | string; decision?: string }
    ) =>
      request.post(
        '/mine/pateMineFigureMarkerC/deleteById',
        {
          proId,
          ...data
        },
        { loadingMode: 'mutate' }
      ),
    figureMarkerReconcile: (
      proId: number | string,
      data: {
        fileId: number | string
        xml?: string
        decisions?: { refCode: string; action: string }[]
      }
    ) =>
      request.post(
        '/mine/pateMineFigureMarkerC/reconcile',
        {
          proId,
          ...data
        },
        { loadingMode: 'mutate' }
      ),
    figureMarkerParseCsv: (proId: number | string, formData: FormData) => {
      if (!formData.has('proId')) {
        formData.append('proId', String(proId))
      }
      return request.post('/mine/pateMineFigureMarkerC/parseCsv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
        loadingMode: 'mutate'
      })
    },
    /** 附图预制引线列表（不含 cellsXml；校验挖掘项目归属） */
    figureLeadLinePresetList: (proId: number | string) =>
      request.post(
        '/mine/figure-lead-line-presets/list',
        { proId },
        { loadingMode: 'query' }
      ),
    /** 附图预制引线详情（拖放落画布） */
    figureLeadLinePresetDetail: (
      proId: number | string,
      data: { presetCode: string }
    ) =>
      request.post(
        '/mine/figure-lead-line-presets/detail',
        { proId, presetCode: data.presetCode },
        { loadingMode: 'silent' }
      ),
    exportProjectBundle: data =>
      request.post('/mine/pateMineExport/exportBundle', data, {
        responseType: 'blob',
        timeout: 300000,
        loadingMode: 'mutate'
      }) // 导出项目文件压缩包
  },
  pay: {
    /** 与 /portal/purchase/payChannels 等价，走已放行的门户匿名接口 */
    channelConfig: () =>
      request.get('/portal/purchase/payChannels', {}, { noLoading: true }),
    listEnabledPlans: () => request.get('/pay/pricePlan/listEnabled'),
    createOrder: data => request.post('/pay/order/create', data),
    payOrder: params => request.post('/pay/order/pay', {}, { params }),
    mockPay: params => request.post('/pay/order/mockPay', {}, { params }),
    orderStatus: (params, config = {}) =>
      request.get('/pay/order/status', params, config),
    myOrders: data => request.post('/pay/order/myOrders', data),
    myQuota: () => request.get('/pay/quota/my'),
    canCreateProject: (params: { moduleCode: string }) =>
      request.get('/pay/quota/canCreate', params),
    activatePendingEntitlement: (params: { entId: number | string }) =>
      request.post('/pay/quota/activatePending', {}, { params }),
    refundable: (params, config = {}) =>
      request.get('/pay/order/refund/refundable', params, config),
    uploadRefundImage: data =>
      request.post('/pay/order/refund/uploadImage', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      }),
    applyRefund: data => request.post('/pay/order/refund/apply', data),
    adminOrderList: data => request.post('/pay/admin/order/list', data),
    adminRefundApply: data => request.post('/pay/admin/refund/apply', data),
    adminRefundList: data => request.post('/pay/admin/refund/list', data),
    adminRefundAudit: data => request.post('/pay/admin/refund/audit', data)
  },
  dataQuery: {
    userList: data => request.post('/data-query/user/list', data),
    userProjects: data => request.post('/data-query/user/projects', data)
  },
  system: {
    userList: data => request.post('/security/user/list', data),
    /** 与后端 BizConstants.DEFAULT_ROLE_ID 一致：会员通用角色 */
    userRoleIds: params => request.get('/security/user/roleIds', params),
    userAssignRole: data => request.post('/security/user/assignRole', data),
    userChangeValidFlag: data =>
      request.post('/security/user/changeValidFlag', data),
    userChangeInnerFlag: data =>
      request.post('/security/user/changeInnerFlag', data),
    userQuotaSummary: params =>
      request.get('/security/user/quotaSummary', params),
    userAssignQuota: data => request.post('/security/user/assignQuota', data),
    adminUserProjectList: data => request.post('/admin/userProject/list', data),
    adminUserProjectDetail: data =>
      request.post('/admin/userProject/detail', data),
    userApplyList: data => request.post('/security/userApply/list', data),
    userApplyAudit: data => request.post('/security/userApply/audit', data),
    roleList: data => request.post('/security/role/list', data),
    roleOptions: () => request.post('/security/role/options'),
    roleAdd: data => request.post('/security/role/add', data),
    roleUpdate: data => request.post('/security/role/update', data),
    roleAssignMenu: data => request.post('/security/role/assignMenu', data),
    roleMenuIds: params => request.get('/security/role/menuIds', params),
    roleChangeValidFlag: data =>
      request.post('/security/role/changeValidFlag', data),
    menuList: data => request.post('/security/menu/list', data),
    // 系统日志 / 操作日志
    userOprtLogList: data => request.post('/security/log/userOprt/list', data),
    /** 系统日志模块选项 */
    userOprtLogModuleOptions: () =>
      request.get('/security/log/userOprt/moduleOptions'),
    userOperLogList: data => request.post('/security/log/userOper/list', data),
    /** 操作日志模块选项 */
    userOperLogModuleOptions: () =>
      request.get('/security/log/userOper/moduleOptions'),
    releaseNoteList: data => request.post('/security/releaseNote/list', data),
    releaseNoteDetail: params =>
      request.get('/security/releaseNote/detail', params),
    releaseNoteAdd: data => request.post('/security/releaseNote/add', data),
    releaseNoteUpdate: data =>
      request.post('/security/releaseNote/update', data),
    releaseNoteChangeValidFlag: data =>
      request.post('/security/releaseNote/changeValidFlag', data),
    figureLeadLinePresetList: data =>
      request.post('/security/figureLeadLinePreset/list', data),
    figureLeadLinePresetDetail: params =>
      request.get('/security/figureLeadLinePreset/detail', params),
    figureLeadLinePresetAdd: data =>
      request.post('/security/figureLeadLinePreset/add', data),
    figureLeadLinePresetUpdate: data =>
      request.post('/security/figureLeadLinePreset/update', data),
    figureLeadLinePresetChangeEnabled: data =>
      request.post('/security/figureLeadLinePreset/changeEnabled', data),
    figureLeadLinePresetDelete: data =>
      request.post('/security/figureLeadLinePreset/delete', data),
    figurePromptList: (params?: { kind?: string }) =>
      request.get('/security/figurePrompt/list', params),
    figurePromptDetail: (params: { promptKey: string }) =>
      request.get('/security/figurePrompt/detail', params),
    figurePromptSave: (data: { promptKey: string; promptContent: string }) =>
      request.post('/security/figurePrompt/save', data),
    figurePromptChangeEnabled: (data: {
      promptKey: string
      enabled: boolean
    }) => request.post('/security/figurePrompt/changeEnabled', data),
    figurePromptHist: (params: {
      promptKey: string
      pageNum?: number
      pageSize?: number
    }) => request.get('/security/figurePrompt/hist', params),
    figurePromptHistDetail: (params: { histId: number }) =>
      request.get('/security/figurePrompt/histDetail', params),
    figurePromptRestoreHist: (data: { histId: number }) =>
      request.post('/security/figurePrompt/restoreHist', data),
    figurePromptRestoreCode: (data: { promptKey: string }) =>
      request.post('/security/figurePrompt/restoreCode', data),
    figurePromptExportSql: (data: { startTime?: string; endTime?: string }) =>
      request.post('/security/figurePrompt/exportSql', data),
    dashboardOverview: () => request.get('/security/dashboard/overview'),
    dashboardTimeseries: data =>
      request.post('/security/dashboard/timeseries', data),
    dashboardProjectDistribution: () =>
      request.get('/security/dashboard/projectDistribution'),
    userStatList: data => request.post('/security/userStat/list', data),
    userStatDetail: params => request.get('/security/userStat/detail', params),
    feedbackList: data => request.post('/security/feedback/list', data),
    feedbackDetail: params => request.get('/security/feedback/detail', params),
    feedbackReply: data => request.post('/security/feedback/reply', data),
    feedbackClose: params =>
      request.post('/security/feedback/close', {}, { params }),
    pricePlanList: () => request.post('/pay/admin/pricePlan/list'),
    pricePlanSave: data => request.post('/pay/admin/pricePlan/save', data),
    pricePlanToggle: data => request.post('/pay/admin/pricePlan/toggle', data)
  },
  //专利撰写
  patentDrafting: {
    getTemplatesByScopeId: params =>
      request.get('/patent-template/list-by-scope', params), // 根据技术领域获取模板列表
    // AI功能API
    aiChat: data =>
      request.post('/ai/chat', data, {
        timeout: 300000
      }), //AI对话
    summarizeDocument: data => request.post(`/ai/summarize/${data.fileId}`), // 文档摘要生成
    assistPatentWriting: data => request.post('/ai/assist-writing', data), // 专利撰写辅助
    translate: data => request.post('/ai/translate', data), // 翻译功能
    chatWithTemplate: data => request.post('/ai/chat-with-template', data), // 使用模板对话
    chatByTemplate: data =>
      request.post('/ai/chat-by-template', data, {
        timeout: 300000
      }), // 基于提示词模板的统一AI对话（后端组装prompt）
    generatePatentSection: data =>
      request.post('/ai/generate-patent-section', data, {
        timeout: 120000
      }), // 生成专利申请文件指定部分
    generateAllPatentSections: data =>
      request.post('/ai/generate-all-patent-sections', data, {
        timeout: 600000
      }), // 批量生成所有专利申请文件部分
    generatePatentFromTemplate: data =>
      request.post('/ai/generate-patent-from-template', data, {
        timeout: 600000 // 10分钟超时，批量生成需要很长时间
      }), // 基于专利申请模板直接生成完整专利文件（用于第4步，不使用嵌入提示词）
    generateAndSavePatent: data =>
      request.post('/ai/generate-and-save-patent', data, {
        timeout: 600000 // 10分钟超时，批量生成需要很长时间
      }), // 批量生成并保存专利申请文件所有部分到数据库
    analyzeImage: (data, config = {}) =>
      request.post('/ai/analyze-image', data, {
        timeout: 60000, // 1分钟超时
        ...config
      }), // AI分析图片
    analyzeDisclosure: data =>
      request.post('/ai/analyze-disclosure', data, {
        timeout: 60000 // 1分钟超时
      }), // AI分析披露文件内容用于专利检索
    getPatentSectionsConfig: () => request.get('/ai/patent-sections-config'), // 获取专利章节配置信息
    /** 具体实施方式：撰写计划（独立接口，不走 chat-by-template） */
    detailedDescriptionDefaultWorkSteps: params =>
      request.get('/patent/detailed-description/default-work-steps', params),
    detailedDescriptionGeneratePlan: data =>
      request.post('/patent/detailed-description/generate-plan', data, {
        timeout: 30000000
      }),
    detailedDescriptionLatest: params =>
      request.get('/patent/detailed-description/latest', params),
    detailedDescriptionGetPlan: planId =>
      request.get(`/patent/detailed-description/${planId}`),
    detailedDescriptionGenerateSection: data =>
      request.post('/patent/detailed-description/generate-section', data, {
        timeout: 300000
      }),
    /** 按小节标题生成 HTML（提示语来自 plan_prompt 表；不写回 plan_json） */
    detailedDescriptionGenerateSectionHtml: data =>
      request.post('/patent/detailed-description/generate-section-html', data, {
        timeout: 300000
      }),
    /** 更新撰写计划 JSON（节顺序、references 等整包保存） */
    detailedDescriptionUpdatePlanJson: data =>
      request.post(
        `/patent/detailed-description/${data.planId}/plan-json`,
        {
          projectId: data.projectId,
          planJson: data.planJson
        },
        { timeout: 120000 }
      ),
    /** 具体实施方式计划 AI 对话——历史列表 */
    ddPlanAiDialogList: (params: {
      projectId: number | string
      planId: number | string
    }) => request.get('/patent/dd-plan-ai-dialog/list', params),
    /** 开始新对话（归档当前会话） */
    ddPlanAiDialogNewSession: (params: {
      projectId: number | string
      planId: number | string
    }) =>
      request.post('/patent/dd-plan-ai-dialog/new-session', null, { params }),
    /** 恢复历史会话 */
    ddPlanAiDialogResumeSession: (params: {
      projectId: number | string
      planId: number | string
      sessionSeq: number
    }) =>
      request.post('/patent/dd-plan-ai-dialog/resume-session', null, {
        params
      }),
    /**
     * 专利检索API
     */
    getPatentDetails: params => request.get(`/patent-search/details/${params}`), // 通过专利公开号查询专利详情
    getPatentDescription: params =>
      request.get(`/patent-search/description/${params}`), // 通过专利公开号获取专利说明书（包含完整的description_text）
    /** 按公开号或申请号实时获取八月瓜全文附图（后端可换号；不落库） */
    getPatentImages: (pn: string) =>
      request.get(`/patent-search/images/${encodeURIComponent(pn)}`),
    searchPatents: data => request.post('/patent-search/search', data), // 专利搜索
    quickSearch: params => request.get('/patent-search/search', params), // 快速搜索
    getPatentLegalStatus: params =>
      request.get(`/patent-search/legal-status/${params.pn}`), // 获取专利法律状态

    getPatentCitations: params =>
      request.get(`/patent-search/citations/${params.pn}`), // 获取专利引用信息
    getSimilarPatents: params =>
      request.get(`/patent-search/similar/${params.pn}`, params), // 获取相似专利
    intelligentPatentSearch: data =>
      request.post('/patent-search/intelligent-search', data), // AI智能专利检索
    semanticSearch: data =>
      request.post('/patent-search/semantic-search', data, {
        timeout: 300000
      }), // 语义检索(无首图) - 八月瓜API
    advancedPatentSearch: data =>
      request.post('/patent-search/advanced-search', data), // 高级专利检索
    /**
     * 项目文件API
     */
    getProjectFiles: params => request.get(`/projects/${params}/files`), // 获取项目的所有文件
    getProjectFilesByType: params =>
      request.get(
        `/projects/${params.projectId}/files/by-type/${params.fileType}`
      ), // 根据文件类型获取文件列表
    listPatentDrawingsForContext: (params, config = {}) =>
      request.get(
        `/projects/${params.projectId}/files/patent-drawings/for-context/${params.contextFileId}`,
        undefined,
        { timeout: 300000, ...config }
      ),
    listPatentDrawingsByOwner: (params, config = {}) =>
      request.get(
        `/projects/${params.projectId}/files/patent-drawings/by-owner/${params.ownerFileId}`,
        undefined,
        config
      ),
    listAllPatentDrawingsForProject: params =>
      request.get(
        `/projects/${params.projectId}/files/patent-drawings/all`,
        undefined,
        { timeout: 300000 }
      ),
    /** 撰写附图编辑：diagramCode 为空时内联预览（与挖掘 figurePreviewDataUri 语义一致） */
    getPatentDrawingPreviewDataUri: params =>
      request.get(
        `/projects/${params.projectId}/files/patent-drawings/${params.fileId}/preview-data-uri`
      ),
    /** 撰写：新增附图图示（与挖掘 save 对齐，落库 project_file.diagram_code） */
    savePatentDrawing: data =>
      request.post(
        `/projects/${data.projectId}/files/patent-drawings/save`,
        {
          fileName: data.fileName,
          diagramCode: data.diagramCode,
          fileContent: data.fileContent,
          previewImageBase64: data.previewImageBase64,
          existingPreviewFilePath: data.existingPreviewFilePath,
          ownerFileId: data.ownerFileId,
          aiBase64Ready: data.aiBase64Ready
        },
        { timeout: 300000 }
      ),
    /** 撰写：更新已有附图图示（与挖掘 update 对齐） */
    updatePatentDrawing: data =>
      request.post(
        `/projects/${data.projectId}/files/patent-drawings/update`,
        {
          fileId: data.fileId,
          fileName: data.fileName,
          diagramCode: data.diagramCode,
          previewImageBase64: data.previewImageBase64,
          existingPreviewFilePath: data.existingPreviewFilePath,
          aiBase64Ready: data.aiBase64Ready
        },
        { timeout: 300000 }
      ),
    bindPatentDrawingsToOwner: data =>
      request.post(
        `/projects/${data.projectId}/files/patent-drawings/bindOwner`,
        { drawingIds: data.drawingIds, ownerFileId: data.ownerFileId }
      ),
    getFile: (params, config = {}) =>
      request.get(
        `/projects/${params.projectId}/files/${params.fileId}`,
        undefined,
        config
      ), // 获取文件详情
    deleteFile: params =>
      request.delete(`/projects/${params.projectId}/files/${params.fileId}`), // 删除文件
    /** 批量删除 project_file（单次最多 80 条） */
    batchDeleteFiles: data =>
      request.post(`/projects/${data.projectId}/files/batch-delete`, {
        fileIds: data.fileIds
      }),
    uploadFile: (data, config = {}) => {
      const formData = new FormData()
      formData.append('file', data.file)
      formData.append('fileType', data.fileType)
      if (data.remark != null && data.remark !== '') {
        formData.append('remark', data.remark)
      }
      if (
        data.ownerFileId != null &&
        data.ownerFileId !== '' &&
        !Number.isNaN(Number(data.ownerFileId))
      ) {
        formData.append('ownerFileId', String(data.ownerFileId))
      }
      if (data.analyzePatentDrawing === true) {
        formData.append('analyzePatentDrawing', 'true')
      }
      return request.post(
        `/projects/${data.projectId}/files/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000,
          ...config
        }
      )
    }, // 上传单个文件
    analyzePatentDrawingRemark: (params, config = {}) =>
      request.post(
        `/projects/${params.projectId}/files/${params.fileId}/patent-drawing/analyze-remark`,
        {},
        {
          timeout: 300000,
          ...config
        }
      ),
    analyzePatentDrawingRemarks: (params, config = {}) =>
      request.post(
        `/projects/${params.projectId}/files/patent-drawings/analyze-remarks`,
        {
          fileIds: params.fileIds,
          concurrency: params.concurrency
        },
        {
          timeout: 300000,
          ...config
        }
      ),
    uploadFiles: data =>
      request.post(`/projects/${data.projectId}/files/upload-batch`, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }), // 批量上传文件
    updateFileContent: data =>
      request.post(
        `/projects/${data.projectId}/files/${data.fileId}/update-content`,
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      ), // 更新文件内容（创建新版本）
    // 后端使用 @RequestParam 接收 remark/fileName/sortOrder，须走 query，不能发 JSON body
    updateFileInfo: (data, config = {}) => {
      const params: Record<string, string | number> = {}
      if (data.fileName != null && data.fileName !== '') {
        params.fileName = data.fileName
      }
      if (data.sortOrder != null) {
        params.sortOrder = data.sortOrder
      }
      if (Object.prototype.hasOwnProperty.call(data, 'remark')) {
        params.remark = data.remark ?? ''
      }
      return request.put(
        `/projects/${data.projectId}/files/${data.fileId}`,
        undefined,
        { params, ...config }
      )
    },
    getFileVersionHistory: params =>
      request.get(
        `/projects/${params.projectId}/files/${params.fileId}/versions`
      ), // 获取文件版本历史
    listPatentApplicationVersions: params =>
      request.get(
        `/projects/${params.projectId}/files/${params.fileId}/patent-application/versions`
      ), // 专利申请文件同族版本列表（下拉）
    savePatentApplicationAsNewVersion: data =>
      request.post(
        `/projects/${data.projectId}/files/${data.fileId}/patent-application/save-as`,
        {
          html: data.html,
          sectionTitle: data.sectionTitle
        }
      ), // 专利申请另存为新版本（HTML）
    getFileStatistics: params =>
      request.get(`/projects/${params.projectId}/files/statistics`), // 获取项目文件统计信息
    downloadFile: params =>
      request.get(
        `/projects/${params.projectId}/files/${params.fileId}/download`,
        undefined,
        {
          responseType: 'blob'
        }
      ), // 下载文件
    parseDocumentContent: (params, config = {}) =>
      request.get(
        `/projects/${params.projectId}/files/${params.fileId}/parse`,
        undefined,
        {
          timeout: 300000,
          ...config
        }
      ), // 解析文档内容为HTML格式

    extractImagesFromDocument: data =>
      request.post(
        `/projects/${data.projectId}/files/${data.fileId}/extract-images`,
        undefined,
        {
          timeout: 300000
        }
      ), // 从文档中提取图片（返回Base64，旧接口保留）

    extractImagesWithDesc: (data, config = {}) =>
      request.post(
        `/projects/${data.projectId}/files/${data.fileId}/extract-images-with-desc`,
        undefined,
        {
          timeout: 600000, // PDF 抽图和 OSS 归档可能耗时较长
          ...config
        }
      ), // 从文档提取图片（上传OSS返回URL）并归档，AI描述由批量识别接口补齐

    draftingFileAccessUrlBatch: data =>
      request.post(
        `/projects/${data.projectId}/files/access-url-batch`,
        {
          fileIdList: data.fileIdList
        },
        { noLoading: true, noError: true }
      ),

    //项目相关
    createProject: (data, config = {}) =>
      request.post('/projects', data, config), // 创建项目
    updateProject: data => request.put(`/projects/${data.id}`, data), // 更新项目
    deleteProject: params => request.delete(`/projects/${params}`), // 删除项目
    getProject: params => request.get(`/projects/${params.id}`), // 获取项目详情
    getMyProjects: () => request.get('/projects/my-projects'), // 获取我的项目列表---不分页
    /** 我的项目 + 各项目文件列表，一次返回（左侧树；仅元数据，不含正文） */
    getMyProjectsFilesList: () =>
      request.get('/projects/myProjectsFilesList', {}, { timeout: 120000 }),
    getProjectsPage: params => request.get('/projects/page', params), // 分页查询项目
    searchProjects: params => request.get('/projects/search', params), // 搜索项目
    getProjectStatistics: () => request.get('/projects/statistics'), // 获取项目统计
    generateSectionContent: data =>
      request.post('/patent-template/generate-section', data), //生成章节内容
    /** 重命名项目（body: { name }） */
    renameProject: (projectId: number | string, name: string) =>
      request.put(`/projects/${projectId}/rename`, { name }),
    /** 附图标记（动词风格，与专利挖掘一致） */
    figureMarkerList: (projectId: number | string, fileId?: number | string) =>
      request.post(`/projects/${projectId}/figure-markers/list`, {
        ...(fileId != null && fileId !== '' ? { fileId } : {})
      }),
    figureMarkerExtractStatus: (projectId: number | string, config = {}) =>
      request.post(
        `/projects/${projectId}/figure-markers/extractStatus`,
        {},
        { noLoading: true, timeout: 15000, ...config }
      ),
    figureMarkerBatchSave: (
      projectId: number | string,
      data: { items: unknown[] }
    ) => request.post(`/projects/${projectId}/figure-markers/batchSave`, data),
    figureMarkerSave: (
      projectId: number | string,
      data: {
        id?: number | null
        refCode: string
        labelZh: string
        fileId?: number | string
        canvasXml?: string
      }
    ) =>
      request.post(`/projects/${projectId}/figure-markers/save`, data, {
        noError: true
      }),
    figureMarkerDeleteById: (
      projectId: number | string,
      data: { id: number; fileId?: number | string; decision?: string }
    ) => request.post(`/projects/${projectId}/figure-markers/deleteById`, data),
    figureMarkerReconcile: (
      projectId: number | string,
      data: {
        fileId: number | string
        xml?: string
        decisions?: { refCode: string; action: string }[]
      }
    ) => request.post(`/projects/${projectId}/figure-markers/reconcile`, data),
    molecularFormulaList: (
      projectId: number | string,
      data: { ownerFileId: number | string }
    ) => request.post(`/projects/${projectId}/molecular-formulas/list`, data),
    molecularFormulaAccessUrlBatch: (
      projectId: number | string,
      data: { formulaIdList: Array<number | string> }
    ) =>
      request.post(
        `/projects/${projectId}/molecular-formulas/access-url-batch`,
        data,
        { noLoading: true, noError: true }
      ),
    molecularFormulaUpload: (projectId: number | string, formData: FormData) =>
      request.post(
        `/projects/${projectId}/molecular-formulas/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000
        }
      ),
    molecularFormulaEditorDetail: (
      projectId: number | string,
      data: { id: number | string }
    ) =>
      request.post(
        `/projects/${projectId}/molecular-formulas/editor-detail`,
        data,
        { noLoading: true }
      ),
    molecularFormulaSaveDrawn: (
      projectId: number | string,
      formData: FormData
    ) =>
      request.post(
        `/projects/${projectId}/molecular-formulas/save-drawn`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000
        }
      ),
    molecularFormulaImportDisclosure: (
      projectId: number | string,
      data: {
        ownerFileId: number | string
        structureKind?: 'GENUS' | 'SPECIES'
        items: { drawingId: number | string; relatedIds?: string[] }[]
      }
    ) =>
      request.post(
        `/projects/${projectId}/molecular-formulas/import-disclosure`,
        data,
        { timeout: 300000 }
      ),
    molecularFormulaSave: (
      projectId: number | string,
      data: {
        id: number | string
        description?: string
        structureKind?: 'GENUS' | 'SPECIES'
        displayLabel?: string
        structureSmiles?: string
        groups?: {
          nameHtml?: string
          introductionHtml?: string
          preferredHtml?: string[][]
        }[]
      },
      config: Record<string, unknown> = {}
    ) =>
      request.post(`/projects/${projectId}/molecular-formulas/save`, data, {
        noLoading: true,
        ...config
      }),
    molecularFormulaDeleteById: (
      projectId: number | string,
      data: { id: number | string }
    ) =>
      request.post(
        `/projects/${projectId}/molecular-formulas/deleteById`,
        data
      ),
    molecularFormulaExcelPreview: (
      projectId: number | string,
      formData: FormData
    ) =>
      request.post(
        `/projects/${projectId}/molecular-formulas/excel-preview`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 90000
        }
      ),
    molecularFormulaGenerateGenus: (
      projectId: number | string,
      data: {
        ownerFileId: number | string
        speciesIds: Array<number | string>
        mode: 'CREATE' | 'UPDATE'
        targetGenusId?: number | string
      }
    ) =>
      request.post(
        `/projects/${projectId}/molecular-formulas/generate-genus`,
        data,
        { timeout: 120000 }
      ),
    molecularFormulaExcelImport: (
      projectId: number | string,
      formData: FormData
    ) =>
      request.post(
        `/projects/${projectId}/molecular-formulas/excel-import`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        }
      ),
    /** 上传 CSV/Excel 解析附图标记（multipart，字段名 file，支持 .csv/.xlsx/.xls） */
    figureMarkerParseCsv: (projectId: number | string, formData: FormData) =>
      request.post(`/projects/${projectId}/figure-markers/parseCsv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000
      }),
    /** 附图预制引线列表（不含 cellsXml） */
    figureLeadLinePresetList: (projectId: number | string) =>
      request.post(`/projects/${projectId}/figure-lead-line-presets/list`, {}),
    /** 附图预制引线详情（拖放落画布） */
    figureLeadLinePresetDetail: (
      projectId: number | string,
      data: { presetCode: string }
    ) =>
      request.post(
        `/projects/${projectId}/figure-lead-line-presets/detail`,
        data,
        { noLoading: true }
      ),
    /** 重命名项目文件（body: { name }） */
    renameProjectFile: (
      projectId: number | string,
      fileId: number | string,
      name: string
    ) => request.put(`/projects/${projectId}/files/${fileId}/rename`, { name }),
    getSectionsByFileId: fileId => request.get(`/sections/file/${fileId}`), // 按文件 ID 拉取章节列表
    getSections: data => request.post('/sections', data, { timeout: 3000000 }),
    exportDocument: data =>
      request.post('/export/document', data, {
        responseType: 'blob'
      }), //导出文件（HTML 入参，匿名/通用）
    /** 专利申请书：后端读库拼接正文与附图后导出 docx（需登录，fileId 与撰写页 effectiveFileId 一致） */
    exportPatentApplicationDocx: (
      projectId: number | string,
      fileId: number | string,
      htmlContent?: string | null,
      allowFormulaFallback = false
    ) =>
      request.post(
        `/projects/${projectId}/files/${fileId}/export/patent-docx`,
        { htmlContent: htmlContent || null, allowFormulaFallback },
        {
          responseType: 'blob',
          timeout: 300000
        }
      ),
    preflightPatentApplicationDocx: (
      projectId: number | string,
      fileId: number | string,
      htmlContent?: string | null
    ) =>
      request.post(
        `/projects/${projectId}/files/${fileId}/export/patent-docx/preflight`,
        { htmlContent: htmlContent || null },
        { timeout: 300000 }
      ),

    // 提示词模板
    getPromptTemplatesByKey: (params: {
      placeholderKey: string
      projectId?: number | string
    }) => request.get('/prompt-template/list-by-key', params),
    getPromptFrames: () => request.get('/prompt-template/frames'),
    getAllPromptTemplates: (params?: { projectId?: number | string }) =>
      request.get('/prompt-template/list-all', params),
    savePromptTemplate: (data: {
      placeholderKey: string
      projectId?: number | string
      title: string
      workSteps: string
    }) => request.post('/prompt-template/save', data),
    updatePromptTemplate: (data: {
      id: number
      title?: string
      workSteps: string
    }) => request.put('/prompt-template/update', data),
    deletePromptTemplate: (id: number) =>
      request.delete(`/prompt-template/delete/${id}`),

    // 形式问题检查
    getFormalCheckRuleList: () => request.get('/formal-check/rule/list'),
    runFormalCheckSingle: (data: {
      ruleId: number
      ruleName?: string
      fileId: number | string
      projectId: number | string
    }) => request.post('/formal-check/run-single', data),
    runFormalCheckBatch: (data: {
      fileId: number | string
      projectId: number | string
      ruleIds?: number[]
    }) =>
      request.post('/formal-check/run-batch', data, {
        timeout: 30000000
      }),
    getFormalCheckResults: params =>
      request.get('/formal-check/results', params),
    aiRepairItem: (data: {
      ruleId: number
      fileId: number | string
      projectId: number | string
      originalText: string
      issue_description: string
      detl_id?: number
      detlId?: number
    }) => request.post('/formal-check/ai-repair', data),
    formalCheckDetailIgnore: (
      detlId: number,
      data: {
        fileId: number | string
        projectId: number | string
        ruleId: number
      }
    ) => request.post(`/formal-check/details/${detlId}/ignore`, data),
    formalCheckDetailMarkManualFixed: (
      detlId: number,
      data: {
        fileId: number | string
        projectId: number | string
        ruleId: number
      }
    ) => request.post(`/formal-check/details/${detlId}/mark-manual-fixed`, data)
  },
  // 答OA
  patentReply: {
    createProject: data => request.post('/reply/pateReplyProjectB/add', data), //创建项目
    updateProject: data =>
      request.post('/reply/pateReplyProjectB/update', data), //更新项目（名称、技术领域）
    getProjectList: data => request.post('/reply/pateReplyProjectB/list', data), //获取项目列表
    deleteProject: params =>
      request.get('/reply/pateReplyProjectB/delete', params), //删除项目
    getMajorFieldList: () => request.post('/reply/pateScopeA/list'), //技术领域（答OA）
    getWorkbenchDetail: params =>
      request.get('/reply/pateReplyWorkbench/detail', params),
    uploadWorkbenchFile: data =>
      request.post('/reply/pateReplyWorkbench/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      }),
    deleteWorkbenchFile: params =>
      request.post('/reply/pateReplyWorkbench/deleteFile', null, { params }),
    uploadCompareFile: data =>
      request.post('/reply/pateReplyWorkbench/uploadCompareFile', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      }),
    deleteCompareFile: params =>
      request.post('/reply/pateReplyWorkbench/deleteCompareFile', null, {
        params
      }),
    renameWorkbenchFile: params =>
      request.post('/reply/pateReplyWorkbench/renameFile', null, { params }),
    renameCompareFile: params =>
      request.post('/reply/pateReplyWorkbench/renameCompareFile', null, {
        params
      }),
    analyzeWorkbench: params =>
      request.post('/reply/pateReplyWorkbench/analyze', null, {
        params,
        timeout: 60000,
        noLoading: true
      }),
    getAnalyzeWorkbenchStatus: params =>
      request.get('/reply/pateReplyWorkbench/analyzeStatus', params, {
        timeout: 60000,
        noLoading: true
      }),
    updateWorkbenchReplyDueDate: params =>
      request.post('/reply/pateReplyWorkbench/updateReplyDueDate', null, {
        params
      }),
    updateReviewOpinionAdoptStatus: params =>
      request.post(
        '/reply/pateReplyWorkbench/updateReviewOpinionAdoptStatus',
        null,
        { params }
      ),
    updateFeatureAnalysisAdoptStatus: params =>
      request.post(
        '/reply/pateReplyWorkbench/updateFeatureAnalysisAdoptStatus',
        null,
        { params }
      ),
    updateFeatureAnalysisSuggestionText: data =>
      request.post(
        '/reply/pateReplyWorkbench/updateFeatureAnalysisSuggestionText',
        data
      ),
    applyClaimSuggestions: data =>
      request.post('/reply/pateReplyWorkbench/applyClaimSuggestions', data, {
        timeout: 180000,
        noLoading: true
      }),
    saveReplyDocument: data =>
      request.post('/reply/pateReplyWorkbench/saveReplyDocument', data, {
        timeout: 120000
      }),
    saveClaimTexts: data =>
      request.post('/reply/pateReplyWorkbench/saveClaimTexts', data, {
        timeout: 120000
      }),
    exportReplyBundle: data =>
      request.post('/reply/pateReplyWorkbench/exportReplyBundle', data, {
        responseType: 'blob',
        timeout: 180000
      }),
    getCompareFilePdf: params =>
      request.get('/reply/pateReplyWorkbench/getCompareFilePdf', params),
    /** 答复文件 · 意见陈述书（独立表与用户提示词） */
    getOpinionStmtDocTemplate: () =>
      request.get('/reply/pateReplyOpinionStmt/docTemplate'),
    getOpinionStmtPromptTemplates: params =>
      request.get('/reply/pateReplyOpinionStmt/promptTemplates', params),
    saveOpinionStmtPromptTemplate: data =>
      request.post('/reply/pateReplyOpinionStmt/promptTemplates', data),
    updateOpinionStmtPromptTemplate: data =>
      request.put('/reply/pateReplyOpinionStmt/promptTemplates', data),
    deleteOpinionStmtPromptTemplate: id =>
      request.delete(`/reply/pateReplyOpinionStmt/promptTemplates/${id}`),
    chatOpinionStmtByTemplate: data =>
      request.post('/reply/pateReplyOpinionStmt/chatByTemplate', data, {
        timeout: 120000,
        noLoading: true
      }),
    uploadReplyImage: data =>
      request.post('/reply/image/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      }),
    getReplyImageSignedUrls: (imgIds: number[]) =>
      request.post('/reply/image/signedUrls', imgIds)
  }
}
